import type { QueryClient } from '@tanstack/react-query'

import { apiClient, getData } from '@/api/client'
import { marketEndpoints } from '@/api/endpointCache'
import { hasCode } from '@/api/errors'
import type { components } from '@/api/schema'
import {
  companyDetailSchema,
  companyFloorsheetPageSchema,
  companyTradesSchema,
  type CompanyDetail,
  type CompanyFloorsheetPage,
  type CompanyTrade,
} from '@/features/company/api/companyDetail'
import type { CompanyListFilters } from '@/features/company/api/keys'
import { MOVERS_MAX_PAGE_SIZE, SEARCH_MAX_LIMIT } from '@/features/market/api/marketApi'
import {
  backendSectorKey,
  displayTimeWithSeconds,
  pageTotal,
} from '@/features/market/api/marketMappers'
import { money, mul, quantise } from '@/lib/money'
import { blankToNull } from '@/schemas/common'
import { companyListPageSchema, type CompanyListPage } from '@/schemas/company'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]
type MoverWire = Wire<'MoverRow'>

const PAGE_SIZE = 20

/**
 * How many `/market/movers/` pages the roster walk will pull before it stops.
 *
 * Six pages of 100 is 600 companies against a register mm-be's own comment puts
 * at "~300 rows" (services/market/fiftytwo.py) — twice the headroom, and a bound
 * so a runaway `total` cannot turn one screen into an unbounded crawl. The walk
 * normally stops on `meta.total_pages` long before this.
 */
const ROSTER_MAX_PAGES = 6

/**
 * One floorsheet print as the trade table's row, before `companyTradeSchema`
 * parses it. `name` is what the table's symbol cell prints under the ticker;
 * the callers that never draw that cell pass the symbol.
 */
const mapTrade = (row: Wire<'FloorsheetTrade'>, name: string) => ({
  id: row.contract_no,
  symbol: row.symbol,
  name,
  time: displayTimeWithSeconds(row.trade_time),
  tradedAt: row.trade_time,
  buyer: row.buyer ?? '—',
  seller: row.seller ?? '—',
  buyerName: blankToNull(row.buyer_name),
  sellerName: blankToNull(row.seller_name),
  kitta: row.quantity,
  rate: row.rate,
  amount: row.amount,
})

/**
 * A count mm-be keeps in a bare `IntegerField`: shares, kitta, trades. The
 * field promises no sign, and a negative count is a broken legacy row rather
 * than a fact, so it reads as not reported.
 */
const countOrNull = (value: number | null): number | null =>
  value === null || value < 0 ? null : value

/**
 * The overview, as the Stock page's `CompanyDetail`, before
 * `companyDetailSchema` parses it. Exported so it is testable on a fixture
 * without a request; see `companyDetail.ts` for which wire field feeds which.
 *
 * `asked` is the symbol the page was opened on, upper-cased. It stands in for
 * a null `profile.symbol` (nullable in mm-be's model, though a company found
 * BY symbol has one) and the symbol stands in for a blank name, so the header
 * always has something true to print.
 */
export const mapOverview = (wire: Wire<'CompanyOverview'>, asked: string) => {
  const { profile, price } = wire
  const symbol = blankToNull(profile.symbol)?.toUpperCase() ?? asked
  const listedShares = countOrNull(profile.listed_shares)
  return {
    symbol,
    name: blankToNull(profile.name) ?? symbol,
    sector: blankToNull(profile.sector_name),
    quote:
      price === null
        ? null
        : {
            ltp: price.ltp,
            changePercent: price.percent_change,
            change: price.point_change,
            open: price.open,
            high: price.high,
            low: price.low,
            close: price.close,
            previousClose: price.previous_close,
            thirtyDayAverage: price.thirty_day_average,
            asOf: price.date,
          },
    // Derived: mm-be serves no market cap on the overview. Listed shares ×
    // the last traded price, rounded the way the server rounds a rupee figure.
    // A company cannot list no shares, so a 0 is the profile not knowing (as
    // Share holding reads it) and the cap is unknown, not Rs 0.
    marketCap:
      price === null || listedShares === null || listedShares === 0
        ? null
        : quantise(mul(money(price.ltp), listedShares)),
    volume: countOrNull(price?.volume ?? null),
    turnover: price?.turnover ?? null,
    transactions: countOrNull(price?.total_trade ?? null),
    week52High: price?.fifty_two_week_high ?? null,
    week52Low: price?.fifty_two_week_low ?? null,
    profile: {
      about: blankToNull(profile.about),
      // `operation_date` is a DRF DateField, so an AD date: its year is the
      // frame's "Founded 1984".
      foundedYear:
        profile.operation_date === null ? null : Number(profile.operation_date.slice(0, 4)),
      website: blankToNull(profile.website),
      phone: blankToNull(profile.phone_number),
      address: blankToNull(profile.address),
      logoUrl: blankToNull(profile.logo_url),
      listedShares,
      floatedShares: countOrNull(profile.floated_shares),
      paidUpCapital: profile.paid_up_capital,
      promoterHolding: profile.promoter_holding,
      publicHolding: profile.public_holding,
      sectorSymbol: blankToNull(profile.sector_symbol),
      companyType: blankToNull(profile.company_type),
      status: blankToNull(profile.status),
      ipoPrice: profile.ipo_price ?? null,
    },
  }
}

/**
 * Every listed company, not the first page of one.
 *
 * ── Why this walks `/market/movers/` and no longer reads `/market/live/` ─────
 *
 * This is the app's canonical "all companies" surface, and it used to be built
 * from `api.live(200)`. `/market/live/` then took a `limit` capped at 200 (it is
 * 500 now), takes no `page` and merges no page meta (mm-be
 * services/market/live.py `MAX_LIMIT`, api/v5/market/views.py `LiveMarketView`),
 * so the list ended at the 200 busiest names out of "~300"
 * (services/market/fiftytwo.py) and reported that ceiling as `total`. Nothing on
 * screen said so, which makes the reasonable reading of the page — "these are
 * the companies" — wrong.
 *
 * `/market/movers/?type=turnover` is the same queryset behind a real pager: both
 * read `is_last` rows for active `TYPE_STOCK` companies with no further filter
 * (`get_movers` with neither `positive_only` nor `negative_only` vs.
 * `get_stock_snapshots`, services/market/repository.py), the view merges
 * `PageMeta`, and mm-be counts the set BEFORE slicing it. So walking it to
 * exhaustion — the shape `watchlistService` already uses — yields the complete
 * register and a `total` that is a fact about the market.
 *
 * `sort=company&order=asc` moves the A→Z ordering to the server, so pages
 * concatenate in order and the client sorts nothing.
 *
 * Two fields improved as a side effect: `point_change` and `volume` now come
 * off the same row as the price, for every company. They used to come from
 * `movers(limit=100)` and `tradingAverage(200)`, each its own bounded window, so
 * a company present in the table could still report a null move or null volume
 * purely because it sat outside one of them.
 *
 * ⚠️ `/market/live/` still needs offset paging for the Live market and Stock
 * prices tables, which cannot switch to this endpoint: `MoverRow` carries no
 * `close`, and their day-range bars draw it. `/market/fifty-two-week/` needed it
 * too until its `limit` ceiling rose to 500 — one call now returns every company.
 */
const listRoster = async (api: ReturnType<typeof marketEndpoints>): Promise<MoverWire[]> => {
  const first = await api.movers({
    type: 'turnover',
    sort: 'company',
    order: 'asc',
    page: 1,
    page_size: MOVERS_MAX_PAGE_SIZE,
  })
  // `total_pages` is what `PageMeta` sends and what this reads. `total` is the
  // fallback rather than `1`, because defaulting a missing page count to one page
  // would silently reintroduce a cap — a narrower one than the bug being fixed.
  const reportedPages =
    first.meta?.total_pages ??
    (first.meta?.total === undefined ? 1 : Math.ceil(first.meta.total / MOVERS_MAX_PAGE_SIZE))
  const totalPages = Math.min(reportedPages, ROSTER_MAX_PAGES)
  if (totalPages <= 1) return first.rows

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      api.movers({
        type: 'turnover',
        sort: 'company',
        order: 'asc',
        page: index + 2,
        page_size: MOVERS_MAX_PAGE_SIZE,
      }),
    ),
  )
  return [first, ...rest].flatMap((page) => page.rows)
}

export const companyService = {
  list: async (
    filters: CompanyListFilters,
    cursor?: string,
    client?: QueryClient,
  ): Promise<CompanyListPage> => {
    const api = marketEndpoints(client)
    const [roster, members, search] = await Promise.all([
      listRoster(api),
      // mm-be names a sector's members only one sector at a time (the
      // heatmap's drill-down), so only a list filtered by sector asks, and
      // asks for that one sector.
      filters.sector === undefined
        ? Promise.resolve(null)
        : api.heatmap(backendSectorKey(filters.sector)),
      filters.search === undefined || filters.search.trim() === ''
        ? Promise.resolve(null)
        : // `SEARCH_MAX_LIMIT`, not 200: `/market/search/` caps `limit` at 50.
          // Asking for 200 was a 400, and inside this `Promise.all` it failed the
          // whole list — typing into the Companies search box errored the screen.
          api.search(filters.search.trim(), SEARCH_MAX_LIMIT),
    ])
    const inSector = members === null ? null : new Set(members.rows.map((row) => row.key))
    const searchMatches = search === null ? null : new Set(search.rows.map((row) => row.key))
    // Both filters run over the COMPLETE register above, so `total` below counts
    // the filtered market and not a window of it.
    const rows = roster
      .filter((row) => searchMatches === null || searchMatches.has(row.key))
      .filter((row) => inSector === null || inSector.has(row.key))
      .map((row) => ({
        symbol: row.key,
        name: row.name,
        sector: filters.sector ?? null,
        ltp: row.ltp,
        point_change: row.change,
        percent_change: row.change_percent,
        volume: row.volume,
      }))
    const start = cursor === undefined ? 0 : Number(cursor)
    const nextStart = start + PAGE_SIZE

    return companyListPageSchema.parse({
      rows: rows.slice(start, nextStart),
      ...(nextStart < rows.length ? { nextCursor: String(nextStart) } : {}),
      total: rows.length,
    })
  },

  /**
   * One company's identity, last-session quote, activity figures and profile,
   * from `/company/{symbol}/overview/` — one request.
   *
   * Resolves `null` on `404 COMPANY_NOT_FOUND`, and only then: that is the
   * page's "Stock not found", a value rather than an error, so the route's
   * loader settles and the screen draws its own branch (docs/route-loading.md,
   * "Not-found answers"). Any other failure still throws. Caught here rather
   * than in the factory because `null` has always been this function's own
   * answer for an unknown symbol, and every caller and mock relies on that.
   */
  detail: async (symbol: string): Promise<CompanyDetail | null> => {
    const key = symbol.trim().toUpperCase()
    try {
      const overview = await getData<Wire<'CompanyOverview'>>(() =>
        apiClient.GET('/api/v5/company/{symbol}/overview/', { params: { path: { symbol: key } } }),
      )
      return companyDetailSchema.parse(mapOverview(overview, key))
    } catch (error) {
      if (hasCode(error, 'COMPANY_NOT_FOUND')) return null
      throw error
    }
  },

  /**
   * One server page of this company's trades, newest first as mm-be orders them.
   *
   * Filtered and paged by the SERVER (`?symbol=&page=&page_size=`), unlike the
   * market-wide floorsheet screen, which reads a fixed 200-row window and
   * filters client-side — that window would silently miss this symbol's trades
   * on any day it did not trade in the first 200 prints.
   */
  floorsheet: async (
    symbol: string,
    page: number,
    pageSize: number,
    name: string,
    client?: QueryClient,
  ): Promise<CompanyFloorsheetPage> => {
    const trades = await marketEndpoints(client).floorsheet({ symbol, page, page_size: pageSize })
    return companyFloorsheetPageSchema.parse({
      rows: trades.rows.map((row) => mapTrade(row, name)),
      total: pageTotal(trades.meta, trades.rows.length),
    })
  },

  /**
   * The `count` biggest prints of this company by amount, for "Largest trades
   * today".
   *
   * Ordered by the SERVER (`order=amount`): sorting the first page of a
   * newest-first log would call the largest of twenty prints the largest of the
   * day. The session is the floorsheet's own latest (`is_last`), which can lag
   * the price date by a day — see `FloorsheetPanel` for how the section names it.
   */
  largestTrades: async (
    symbol: string,
    count: number,
    client?: QueryClient,
  ): Promise<CompanyTrade[]> => {
    const trades = await marketEndpoints(client).floorsheet({
      symbol,
      order: 'amount',
      page: 1,
      page_size: count,
    })
    return companyTradesSchema.parse(trades.rows.map((row) => mapTrade(row, symbol)))
  },
}
